import { bff, createBFFHandlers } from '~/framework/bff';
import { z } from 'zod';
import { getServerSideMonolithClient } from '~/framework';
import {
   Assessment as MonolithAssessment,
   getCourse as getCourseFromMonolith,
} from '~/monolith-api';
import { CommonRequestOptions } from '~/monolith-api/makeRequest';

export type {
   Assessment,
   StageStatus,
   StageFrequency,
   Assignee,
   UserAssignee,
   TeamAssignee,
   TeamMember,
} from '~/monolith-api';

export { isTeamAssignee } from '~/monolith-api';

const getCourseSchema = z.object({
   wikiid: z.number().nullable(),
});

export async function getCourse(wikiid: number | null, options: CommonRequestOptions) {
   const result = await getCourseFromMonolith(wikiid, options);

   if (!result) return result;

   return {
      ...result,
      stages: await Promise.all(
         result.stages.map(async stage => ({
            ...stage,
            assessments: stage.assessments
               ? await Promise.all(
                    stage.assessments.map(assessment => getAssessmentDetails(assessment))
                 )
               : [],
         }))
      ),
   };
}

async function getAssessmentDetails(assessment: MonolithAssessment) {
   switch (assessment.assessmenttype) {
      case 'trainer-sign-off':
         return {
            ...assessment,
            assessmentid: null,
         };
   }
}

export default createBFFHandlers({
   post: async (req, res) => {
      const { wikiid } = getCourseSchema.parse(req.body);
      const monolithClient = getServerSideMonolithClient(req);
      const result = await getCourse(wikiid, {
         apiClient: monolithClient,
      });

      res.status(200).json(result);
   },
});

export const getCourseFromClient = (wikiid: number | null): ReturnType<typeof getCourse> => {
   return bff()
      .post('/courses/_shared/getCourse', {
         wikiid,
      })
      .then(response => response.data);
};

export type Course = Exclude<Awaited<ReturnType<typeof getCourse>>, null>;

export type Stage = Course['stages'][number];

export type GetCourseErrorCode = 'courses.course_not_found';
